import React, { useEffect, useState } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { skip } from 'rxjs/operators'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import { get } from 'libraries/lodash'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import MuiIconButton from '@mui/material/IconButton'
import MuiExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import MuiExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import { WidthResized } from 'services/WindowService'
import selectIsTouchDevice from 'redux/selectors/selectIsTouchDevice'
import TealToolTip from 'components/TealToolTip'
import useOverflowDetection from 'hooks/useOverflowDetection'

const IconButton = styled(MuiIconButton)({
  color: 'white',
  padding: 8
})

const DesktopExpandMoreRounded = styled(MuiExpandMoreRounded)({
  '&.MuiSvgIcon-colorPrimary': {
    color: 'white'
  }
})

const DesktopExpandLessRounded = styled(MuiExpandLessRounded)({
  '&.MuiSvgIcon-colorPrimary': {
    color: 'white'
  }
})

const ShowMoreIcon = ({ expanded }) => expanded ? <DesktopExpandLessRounded /> : <DesktopExpandMoreRounded />

const ExpandMoreButton = ({ handleOnClick, expanded, isOverFlowed }) => {
  const isTouchDevice = useSelector(selectIsTouchDevice)
  const [showButton, setShowButton] = useState(isOverFlowed || expanded)

  useEffect(() => {
    const subscriber = WidthResized.pipe(
      skip(1)
    ).subscribe((x) => {
      setShowButton(isOverFlowed || expanded)
    })

    return () => {
      subscriber.unsubscribe()
    }
  }, [isOverFlowed, expanded, setShowButton])

  // required because isOverflowed isn't detected on initial render
  // We can't use isOverflowed or isOverflowed || expanded directly due to the interaction it has when
  // the panels are minimised where for a brief time expanded is false
  // but the panel is not yet overflowed
  useEffect(() => {
    if (isOverFlowed) {
      setShowButton(true)
    }
  }, [isOverFlowed])

  if (!showButton) {
    return null
  }

  return (
    <div className='expand-more-button-container w-100 h2 tc absolute bottom0'>
      <TealToolTip title={expanded ? 'Show less' : 'Show more'} placement='right'>
        <IconButton
          aria-label='expand button'
          style={{
            color: '#232A3A'
          }}
          classes={{
            root: cx(
              'mt2 outline-0 o-80',
              { 'icon-button-bg-color-active': isTouchDevice || expanded }
            )
          }}
          onClick={handleOnClick}
        >
          <ShowMoreIcon expanded={expanded} />
        </IconButton>
      </TealToolTip>
    </div>
  )
}

const ContentPanel = ({ panelTitle, panelContent, height }) => {
  const [expanded, setExpanded] = useState(false)
  const { containerRef, contentRef, isOverflowing } = useOverflowDetection()
  const handleShowMoreButtonOnClick = () => setExpanded(expanded => !expanded)
  const title = get(panelTitle, '[0].text')

  return (
    <div className='bg-twilight-grey relative' style={{ minHeight: height }} data-testid='content-panel'>
      <div
        ref={containerRef}
        className='overflow-detector'
        style={{
          maxHeight: expanded ? 2000 : height,
          transition: expanded ? 'max-height 0.5s ease-in' : 'max-height 0.6s ease-out',
          transitionDelay: expanded ? '0.25s' : '0.0s',
          overflow: 'hidden'
        }}
      >
        <div ref={contentRef}>
          <h2 className='ma0 ph6 pt4 black fw6 f4 lh-headline'>
            {title}
          </h2>
          <div className='announcements-content ph6 pb6 pt2 f4' style={{ overflow: 'hidden' }}>
            <PrismicRichText field={panelContent} />
          </div>
        </div>
      </div>
      {expanded ? <div className='h2' /> : null}
      <ExpandMoreButton
        isOverFlowed={isOverflowing}
        expanded={expanded}
        handleOnClick={handleShowMoreButtonOnClick}
      />
    </div>
  )
}

const NoResultPanel = ({
  panel_title: [{ text: 'No Updates' }],
  panel_content: null
})

const ContentPanels = ({ CMS_DATA, setHomePageFcUpdatesIsLoaded }) => {
  const panels = get(CMS_DATA, 'results[0].data.panel', [NoResultPanel])
  const totalHeight = 1728 - (16 * (panels.length - 1))
  const height = totalHeight / panels.length

  useEffect(() => {
    setHomePageFcUpdatesIsLoaded(true)
  }, [])

  return (
    <Grid container className='bg-page-background h-100' spacing={2}>
      {panels.map((panel, idx) => (
        <Grid item xs={12} sm={6} md={6} lg={12} key={idx}>
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <ContentPanel panelTitle={panel.panel_title} panelContent={panel.panel_content} {...{ height }} />
          </Box>
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <ContentPanel panelTitle={panel.panel_title} panelContent={panel.panel_content} {...{ height: 595 }} />
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

export default ContentPanels
